/**
 * @file CrossfitEnfants.tsx
 * @summary Article de blog — CrossfitEnfants
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import BlogHeroImage, { getArticleImageUrls, withArticleImages, slugFromUrl } from "@/components/blog/BlogHeroImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, Smile, Brain, Users, ShieldCheck } from "lucide-react";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";

const URL = "https://crossfitchistera.fr/blog/crossfit-enfants";
const SLUG = slugFromUrl(URL);
const IMAGES = getArticleImageUrls(SLUG, 5);
const RESAWOD = "https://social.resawod.com/fr/box/crossfit-chistera/reserver";

const faq = [
  { "@type": "Question", name: "À partir de quel âge mon enfant peut commencer ?", acceptedAnswer: { "@type": "Answer", text: "Les cours CrossFit Kids chez Chistera accueillent les enfants de 6 à 10 ans. Les exercices sont adaptés à leur croissance, sans charges lourdes." } },
  { "@type": "Question", name: "Est-ce dangereux pour la croissance ?", acceptedAnswer: { "@type": "Answer", text: "Non. Le CrossFit Kids ne vise pas la performance ni la charge maximale. C'est de la motricité, du jeu, de la coordination, encadrés par un coach formé." } },
  { "@type": "Question", name: "Combien coûte un abonnement Kids ?", acceptedAnswer: { "@type": "Answer", text: "L'abonnement Cours Kids est à 40 €/mois. Une séance d'essai est possible pour découvrir avant de s'engager." } },
  { "@type": "Question", name: "Les parents peuvent-ils participer ?", acceptedAnswer: { "@type": "Answer", text: "Oui, lors des compétitions parents-enfants organisées chaque année à la box. Un moment fort qui crée des souvenirs partagés." } },
];

const jsonLd = [
  { "@context": "https://schema.org", "@type": "Article", headline: "CrossFit enfants : une activité ludique et sportive", description: "Le CrossFit pour enfants développe coordination, confiance et esprit d'équipe dans une ambiance ludique et familiale.", author: { "@type": "Organization", name: "CrossFit Chistera" }, publisher: { "@type": "Organization", name: "CrossFit Chistera" }, mainEntityOfPage: URL, inLanguage: "fr-FR" },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

const benefices = [
  { icon: Smile, title: "Le plaisir avant tout", desc: "Jeux, parcours, défis — l'effort est camouflé dans l'amusement." },
  { icon: Brain, title: "Coordination & motricité", desc: "Sauter, grimper, lancer, ramper : les bases du mouvement." },
  { icon: Users, title: "Esprit d'équipe", desc: "On s'encourage, on attend les copains, on partage les efforts." },
  { icon: ShieldCheck, title: "Confiance en soi", desc: "Réussir un mouvement, dépasser une appréhension, oser essayer." },
];

export default function CrossfitEnfants() {
  return (
    <>
      <SEO
        title="CrossFit enfants : une activité ludique et sportive"
        description="Le CrossFit pour enfants développe coordination, confiance et esprit d'équipe dans une ambiance ludique et familiale."
        canonical={URL}
        jsonLd={withArticleImages(jsonLd, IMAGES)}
          ogImages={IMAGES}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-32 md:pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <BlogHeroImage slug={SLUG} />
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-3 h-3" /> Accueil</Link>
              <ChevronRight className="w-3 h-3" />
              <span>Blog</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">CrossFit enfants</span>
            </nav>

            <header className="mb-10">
              <span className="inline-block text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">Famille · Lecture ~ 5 min</span>
              <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">Le CrossFit pour enfants : vraiment adapté ?</h1>
              <p className="text-lg text-foreground/85 leading-relaxed mb-8">
                C'est souvent la première réaction des parents : <em>« Le CrossFit, ce n'est pas un peu trop intense pour les enfants ? »</em> Question légitime — et la réponse est claire : le <strong>CrossFit Kids</strong> n'a rien à voir avec le CrossFit adulte. Pas de charges lourdes, pas de chronos serrés. Juste du mouvement, du jeu et du collectif.
              </p>
              <BlogCTA variant="hero" primaryLabel="Découvrir le cours Kids" />
            </header>

            <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mb-12 rounded-r-md">
              <p className="text-sm uppercase tracking-wider text-gold font-heading mb-2">En une phrase</p>
              <p className="text-foreground/90 leading-relaxed">
                Le <strong>CrossFit enfants</strong> chez Chistera, c'est une heure hebdomadaire pour les 6-10 ans, encadrée par un coach formé, qui développe coordination, confiance et esprit d'équipe — dans une ambiance volontairement ludique et familiale.
              </p>
            </aside>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground">
              <h2>On fait quoi pendant un cours enfant ?</h2>
              <p>
                Un cours CrossFit Kids mélange plusieurs activités adaptées à l'âge : parcours de motricité, mini-circuits, jeux d'équipe, exercices au poids du corps, sauts, lancers, mouvements de gymnastique simples. Le tout est <strong>scénarisé</strong> pour que les enfants s'amusent en bougeant — sans même se rendre compte qu'ils s'entraînent.
              </p>
              <p>
                Le coach veille à la qualité du mouvement plutôt qu'à la performance. L'objectif n'est pas de faire des enfants des athlètes, mais de leur donner le goût de l'effort, l'envie de bouger, et les bases motrices qui leur serviront toute la vie.
              </p>
            </div>

            <section className="my-12">
              <div className="grid md:grid-cols-2 gap-4">
                {benefices.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.title} className="border border-border bg-card rounded-lg p-5 hover:border-gold/50 transition-colors">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                        <h3 className="font-heading text-lg text-foreground">{b.title}</h3>
                      </div>
                      <p className="text-foreground/80 text-sm font-body normal-case">{b.desc}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <BlogCTA single primaryLabel="Réserver mon essai gratuit" />

            <div className="my-16 border border-gold/30 bg-charcoal/40 rounded-lg px-6 py-8 text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Une séance d'essai pour votre enfant</h2>
              <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">Réservez via Resawod ou contactez un coach pour discuter de l'âge et du niveau de votre enfant.</p>
              <BlogCTA primaryLabel="Découvrir le cours Kids" />
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground">
              <h2>Les compétitions parents-enfants : un rendez-vous devenu incontournable</h2>
              <p>
                Chaque année, CrossFit Chistera organise des <strong>compétitions familiales parents-enfants</strong> qui rencontrent un vrai succès dans l'Est lyonnais. Un moment fort, joyeux et fédérateur, où les enfants découvrent que leurs parents aussi s'entraînent — et où les parents redécouvrent leurs enfants en pleine action.
              </p>
              <p className="not-prose">
                <a href="https://www.instagram.com/p/DWi2zfaDIHW/?img_index=1&igsh=MXV5MGYxbnVkanRqMw==" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                  → Voir la vidéo Instagram du dernier événement
                </a>
              </p>

              <h2>Une box familiale dans l'Est lyonnais</h2>
              <p>
                CrossFit Chistera est une <strong>salle de CrossFit familiale</strong> à Saint-Pierre-de-Chandieu. De nombreuses familles nous rejoignent depuis Mions, Heyrieux, Toussieu, Chaponnay ou Saint-Bonnet-de-Mure pour les cours adultes et les cours Kids. Tarifs cours Kids : 40 €/mois.
              </p>
            </div>

            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-6">Questions fréquentes</h2>
              <div className="space-y-4">
                {faq.map((q) => (
                  <details key={q.name} className="group border border-border bg-card rounded-lg p-5 open:border-gold/50">
                    <summary className="cursor-pointer font-heading text-lg text-foreground flex justify-between items-center gap-4 list-none">
                      <span>{q.name}</span>
                      <ChevronRight className="w-5 h-5 text-gold shrink-0 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-foreground/85 leading-relaxed font-body normal-case">{q.acceptedAnswer.text}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="my-16">
              <div className="border border-gold/30 bg-gradient-to-br from-charcoal/60 to-charcoal/20 rounded-lg px-6 py-10 text-center">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Inscrivez votre enfant à une séance découverte</h2>
                <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">Réservez via Resawod ou échangez d'abord avec un coach.</p>
                <BlogCTA primaryLabel="Réserver une séance Kids" />
              </div>
            </section>

            <section className="my-12 border-t border-border pt-8">
              <h2 className="text-xl font-heading text-foreground mb-4">À lire aussi</h2>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm font-body normal-case">
                <li><Link to="/blog/quest-ce-que-le-crossfit" className="text-gold hover:underline">→ C'est quoi le CrossFit ?</Link></li>
                <li><Link to="/crossfit-debutant" className="text-gold hover:underline">→ CrossFit débutant</Link></li>
                <li><Link to="/lieux" className="text-gold hover:underline">→ La box & les communes desservies</Link></li>
                <li><Link to="/lexique" className="text-gold hover:underline">→ Lexique CrossFit</Link></li>
              </ul>
            </section>
          </article>
        </main>
        <Footer />
        <BlogStickyCTA label="Découvrir le cours Kids" />
      </div>
    </>
  );
}
